// ─────────────────────────────────────────────────────────────────────────────
// dp-custom.js
// ─────────────────────────────────────────────────────────────────────────────
(function () {
  'use strict';

  // ─── 1. Farb-Logik ───────────────────────────────────────────────────────
  (function () {
    const root = document.documentElement;
    const url = window.location.href.toLowerCase();
    const colors = {
      test:      '#8800ff',
      afterwork: '#2E4A1C',
      techno:    '#0D4075',
      rnb:       '#A82117',
      brunch:    '#FF7538'
    };
    const matched = Object.keys(colors).some(key => {
      if (url.includes(key)) {
        root.style.setProperty('--collection-button-bg-color', colors[key]);
        return true;
      }
      return false;
    });
    if (!matched) root.style.setProperty('--collection-button-bg-color', 'var(--color-text)');
  })();


  // ─── 2. Datum-Hilfsfunktionen ────────────────────────────────────────────
  const TZ = 'Europe/Berlin';
  const DATE_FILTER_KEY = 'filter.p.m.custom.date_filter';

  // P1: eine wiederverwendete Intl.DateTimeFormat-Instanz statt drei
  // toLocaleString()-Aufrufe pro Timestamp. Locale 'en-CA' liefert direkt
  // YYYY-MM-DD, identisch zum bisherigen Key-Format.
  const _keyFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit'
  });
  const _labelFmt = new Intl.DateTimeFormat('de-DE', {
    timeZone: TZ, weekday: 'short', day: 'numeric', month: 'long'
  });
  const _keyCache = new Map();

  function tsToDateKey(ts) {
    let key = _keyCache.get(ts);
    if (key === undefined) {
      key = _keyFmt.format(new Date(ts * 1000));
      _keyCache.set(ts, key);
    }
    return key;
  }

  function tsToLabel(ts) {
    return _labelFmt.format(new Date(ts * 1000));
  }

  function getSelectedDaysFromURL() {
    const params = new URLSearchParams(window.location.search);
    const selected = new Set();
    params.getAll(DATE_FILTER_KEY).forEach(ts => {
      selected.add(tsToDateKey(parseInt(ts, 10)));
    });
    return selected;
  }


  // ─── 3. Active Facets patchen ────────────────────────────────────────────
  // Struktur: <div class="removable-facet">TIMESTAMP<a href="...remove-url...">x</a></div>
  // Ziel: Timestamp → Datum-Label, Duplikate pro Tag ausblenden,
  //       Entfernen-Link des sichtbaren Pills entfernt ALLE Timestamps desselben Tages
  function patchActiveFacets() {
    const container = document.querySelector('.active-facets');
    if (!container) return;

    const facets = [...container.querySelectorAll('.removable-facet')];
    if (!facets.length) return;

    // Nur date_filter Facets — erkennen: Textknoten ist eine reine Zahl (Unix-Timestamp)
    const dateFacets = facets.filter(el => {
      const text = el.childNodes[0]?.textContent?.trim();
      return text && /^\d{9,11}$/.test(text);
    });

    if (!dateFacets.length) return;

    // B4: Signatur statt Einmal-Flag, damit ein In-Place-Update des
    // Containers erneut gepatcht wird, aber unveränderte Inhalte kein
    // unnötiges DOM-Write auslösen.
    const sig = dateFacets.map(el => el.childNodes[0].textContent.trim()).join(',');
    if (container.dataset.fdcPatchedSig === sig) return;
    container.dataset.fdcPatchedSig = sig;

    // Gruppieren nach Datums-Key
    const groups = {}; // dateKey → [facetEl, ...]
    dateFacets.forEach(el => {
      const ts = parseInt(el.childNodes[0].textContent.trim(), 10);
      const key = tsToDateKey(ts);
      if (!groups[key]) groups[key] = [];
      groups[key].push({ el, ts });
    });

    // Pro Gruppe: erste Pille umbenennen, Rest ausblenden
    // Entfernen-Link der ersten Pille: URL ohne ALLE Timestamps dieser Gruppe
    Object.entries(groups).forEach(([key, items]) => {
      const first = items[0];

      // Label setzen
      first.el.childNodes[0].textContent = tsToLabel(first.ts);

      // Entfernen-Link anpassen: alle Timestamps dieser Gruppe aus URL entfernen
      const link = first.el.querySelector('a');
      if (link) {
        const href = link.getAttribute('href');
        const url = new URL(href, window.location.origin);
        const params = url.searchParams;

        // P3: Set der zu entfernenden Timestamps, Parameter genau einmal neu schreiben
        const remove = new Set(items.map(({ ts }) => String(ts)));
        const keep = params.getAll(DATE_FILTER_KEY).filter(v => !remove.has(v));
        params.delete(DATE_FILTER_KEY);
        keep.forEach(v => params.append(DATE_FILTER_KEY, v));

        link.setAttribute('href', url.pathname + (params.toString() ? '?' + params.toString() : ''));
        // aria-label aktualisieren
        link.setAttribute('aria-label', `Filter "${tsToLabel(first.ts)}" entfernen`);
      }

      // Duplikate ausblenden
      items.slice(1).forEach(({ el }) => {
        el.style.display = 'none';
      });
    });
  }


  // ─── 4. Kalender-Styles ──────────────────────────────────────────────────
  // P4: Ideal wäre, dieses CSS dauerhaft ins Theme-Stylesheet zu verschieben
  // (z.B. an assets/base.css oder ein eigenes dp-custom.css anhängen).
  // Bis dahin bleibt es hier als einmaliger Inject.
  function injectCalendarStyles() {
    if (document.getElementById('fdc-styles')) return;
    const style = document.createElement('style');
    style.id = 'fdc-styles';
    style.textContent = `
      .fdc__nav {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 10px;
      }
      .fdc__nav-btn {
        background: none;
        border: none;
        cursor: pointer;
        font-size: 2.2rem;
        line-height: 1;
        color: currentColor;
        opacity: 0.5;
        padding: 0 4px;
        transition: opacity 0.15s;
      }
      .fdc__nav-btn:hover { opacity: 1; }
      .fdc__month-label { font-weight: 500; }
      .fdc__grid {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 3px;
      }
      .fdc__dow {
        font-size: 1.1rem;
        text-align: center;
        padding: 3px 0;
        opacity: 0.4;
      }
      .fdc__day {
        aspect-ratio: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        font-size: 1.2rem;
        position: relative;
        opacity: 0.2;
        user-select: none;
      }
      .fdc__day--event {
        opacity: 1;
        cursor: pointer;
        background: rgba(128,128,128,0.06);
        border: 1px solid rgba(128,128,128,0.15);
        transition: background 0.12s;
      }
      .fdc__day--event:hover { background: rgba(128,128,128,0.14); }
      .fdc__day--selected {
        background: #fcf9f4 !important;
        border: 1.5px solid #c8a97e !important;
        color: #7a5c30;
        font-weight: 500;
        opacity: 1;
      }
      .fdc__dot {
        position: absolute;
        bottom: 3px;
        left: 50%;
        transform: translateX(-50%);
        width: 3px;
        height: 3px;
        border-radius: 50%;
        background: #c8a97e;
        display: block;
      }
      .fdc__day--selected .fdc__dot { background: #a07840; }
      .fdc__footer {
        margin-top: 10px;
        text-align: right;
      }
      .fdc__reset {
        background: none;
        border: none;
        cursor: pointer;
        font-size: 1.1rem;
        opacity: 0.5;
        padding: 0;
        text-decoration: underline;
        color: currentColor;
        transition: opacity 0.15s;
      }
      .fdc__reset:hover { opacity: 1; }
    `;
    document.head.appendChild(style);
  }


  // ─── 5. Kalender-Klasse ──────────────────────────────────────────────────
  const MONTHS_DE = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];

  let _savedMonth = null;
  let _savedYear  = null;

  class FacetDateCalendar {
    constructor(dialog) {
      this.dialog = dialog;
      this.eventMap = {};
      this.selectedDays = new Set();
      this._sig = null;
      this._init();
    }

    _init() {
      this.checkboxes = [...this.dialog.querySelectorAll('input[type="checkbox"]')];
      if (!this.checkboxes.length) return;

      this._buildEventMap();
      this._restoreOrSetMonth();
      this._injectUI();
      this._hideCheckboxes();

      // Auswahl aus URL übernehmen
      getSelectedDaysFromURL().forEach(k => this.selectedDays.add(k));
      this._render();

      this._sig = this._signature(this.checkboxes);
    }

    // B1/Prio 1: Signatur aus verfügbaren Checkboxen + aktueller URL-Auswahl.
    // Ändert sich nichts, bricht refresh() sofort ab -> kein DOM-Write ->
    // die MutationObserver-Schleife terminiert von selbst.
    _signature(boxes) {
      const available = boxes.filter(cb => !cb.disabled).map(cb => cb.value).join(',');
      const selected = [...getSelectedDaysFromURL()].sort().join(',');
      return available + '|' + selected;
    }

    refresh() {
      const boxes = [...this.dialog.querySelectorAll('input[type="checkbox"]')];
      if (!boxes.length) return;

      // B2: UI wurde beim ersten _init() nicht gebaut (keine Checkboxen
      // vorhanden) -> jetzt nachholen statt auf undefined this.grid zu crashen.
      if (!this.grid) {
        this._init();
        return;
      }

      const sig = this._signature(boxes);
      if (sig === this._sig) return;
      this._sig = sig;

      this.checkboxes = boxes;
      this._buildEventMap();
      this._hideCheckboxes();
      this.selectedDays = getSelectedDaysFromURL();
      this._ensureVisibleMonth();
      this._render();
    }

    _buildEventMap() {
      this.eventMap = {};
      this.checkboxes.forEach(cb => {
        // Deaktivierte Checkboxen (0 Treffer unter aktivem Stadt-/Datumsfilter)
        // dürfen keinen Punkt/Klick im Kalender erzeugen.
        if (cb.disabled) return;
        const ts = parseInt(cb.value, 10);
        const key = tsToDateKey(ts);
        if (!this.eventMap[key]) this.eventMap[key] = [];
        this.eventMap[key].push(cb);
      });
    }

    _restoreOrSetMonth() {
      if (_savedMonth !== null && _savedYear !== null) {
        this.month = _savedMonth;
        this.year  = _savedYear;
        return;
      }
      const urlSelected = getSelectedDaysFromURL();
      const keys = Object.keys(this.eventMap).sort();
      const activeKeys = [...urlSelected].sort();
      const startKey = activeKeys.length > 0 ? activeKeys[0] : keys[0];
      if (startKey) {
        const parts = startKey.split('-');
        this.year  = parseInt(parts[0], 10);
        this.month = parseInt(parts[1], 10) - 1;
      } else {
        const now = new Date();
        this.year  = now.getFullYear();
        this.month = now.getMonth();
      }
      _savedMonth = this.month;
      _savedYear  = this.year;
    }

    // B5: Springt auf den ersten Monat mit verfügbaren Events, falls der
    // aktuell angezeigte Monat nach einem Filterwechsel leer ist.
    _ensureVisibleMonth() {
      const keys = Object.keys(this.eventMap).sort();
      if (!keys.length) return;
      const prefix = `${this.year}-${String(this.month + 1).padStart(2, '0')}`;
      if (keys.some(k => k.startsWith(prefix))) return;

      const [y, m] = keys[0].split('-');
      this.year  = parseInt(y, 10);
      this.month = parseInt(m, 10) - 1;
      _savedMonth = this.month;
      _savedYear  = this.year;
    }

    _hideCheckboxes() {
      const wrapper = this.dialog.querySelector('.h-stack');
      if (wrapper) wrapper.style.display = 'none';
    }

    _injectUI() {
      const cal = document.createElement('div');
      cal.className = 'fdc';
      cal.innerHTML = `
        <div class="fdc__nav">
          <button type="button" class="fdc__nav-btn" data-dir="prev" aria-label="Vorheriger Monat">&#8249;</button>
          <span class="fdc__month-label"></span>
          <button type="button" class="fdc__nav-btn" data-dir="next" aria-label="Nächster Monat">&#8250;</button>
        </div>
        <div class="fdc__grid">
          <span class="fdc__dow">Mo</span>
          <span class="fdc__dow">Di</span>
          <span class="fdc__dow">Mi</span>
          <span class="fdc__dow">Do</span>
          <span class="fdc__dow">Fr</span>
          <span class="fdc__dow">Sa</span>
          <span class="fdc__dow">So</span>
        </div>
        <div class="fdc__footer">
          <button type="button" class="fdc__reset">Auswahl zurücksetzen</button>
        </div>
      `;
      this.dialog.appendChild(cal);
      this.monthLabel = cal.querySelector('.fdc__month-label');
      this.grid       = cal.querySelector('.fdc__grid');

      // B6: Reset muss den Kalender auch optisch neu rendern
      cal.querySelector('.fdc__reset').addEventListener('click', () => {
        this.selectedDays.clear();
        this._syncAndSubmit();
        this._render();
      });

      cal.querySelectorAll('.fdc__nav-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.dataset.dir === 'prev') {
            this.month--;
            if (this.month < 0) { this.month = 11; this.year--; }
          } else {
            this.month++;
            if (this.month > 11) { this.month = 0; this.year++; }
          }
          _savedMonth = this.month;
          _savedYear  = this.year;
          this._render();
        });
      });
    }

    _render() {
      this.monthLabel.textContent = `${MONTHS_DE[this.month]} ${this.year}`;
      this._renderDays();
    }

    _renderDays() {
      this.grid.querySelectorAll('.fdc__day').forEach(d => d.remove());

      let startDow = new Date(this.year, this.month, 1).getDay();
      startDow = startDow === 0 ? 6 : startDow - 1;
      const daysInMonth = new Date(this.year, this.month + 1, 0).getDate();

      const fragment = document.createDocumentFragment();

      for (let i = 0; i < startDow; i++) {
        const blank = document.createElement('span');
        blank.className = 'fdc__day';
        fragment.appendChild(blank);
      }

      for (let d = 1; d <= daysInMonth; d++) {
        const mm  = String(this.month + 1).padStart(2, '0');
        const dd  = String(d).padStart(2, '0');
        const key = `${this.year}-${mm}-${dd}`;
        const cbs = this.eventMap[key] || [];
        const hasEvent  = cbs.length > 0;
        const isSelected = this.selectedDays.has(key);

        const span = document.createElement('span');
        span.className = 'fdc__day'
          + (hasEvent   ? ' fdc__day--event'    : '')
          + (isSelected ? ' fdc__day--selected' : '');
        span.textContent = d;

        if (hasEvent) {
          const dot = document.createElement('i');
          dot.className = 'fdc__dot';
          span.appendChild(dot);

          span.addEventListener('click', () => {
            if (this.selectedDays.has(key)) {
              this.selectedDays.delete(key);
            } else {
              this.selectedDays.add(key);
            }
            _savedMonth = this.month;
            _savedYear  = this.year;
            this._syncAndSubmit();
            this._render();

            // Dialog schließen
            this.dialog.removeAttribute('open');
          });
        }

        fragment.appendChild(span);
      }

      // Ein einziger DOM-Write statt bis zu ~42 einzelner appendChild-Aufrufe
      this.grid.appendChild(fragment);
    }

    _syncAndSubmit() {
      this.checkboxes.forEach(cb => {
        cb.checked = this.selectedDays.has(tsToDateKey(parseInt(cb.value, 10)));
      });

      // B7: Auf einer aktivierten Checkbox dispatchen, sonst ignorieren
      // manche Handler das change-Event einer disabled-Checkbox.
      const trigger = this.checkboxes.find(cb => !cb.disabled) || this.checkboxes[0];
      if (trigger) trigger.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }


  // ─── 6. Stadt-Filter ─────────────────────────────────────────────────────
  function initStadtFilter() {
    const dialog = document.getElementById('filter-dialog-filter.p.m.custom.stadt');
    if (!dialog || dialog.dataset.stadtInit) return;
    dialog.dataset.stadtInit = '1';

    if (!dialog.querySelector('#filter-search')) {
      const input = document.createElement('input');
      input.id = 'filter-search';
      input.type = 'text';
      input.placeholder = 'Stadt eingeben, z.B. Stuttgart';
      input.autocomplete = 'off';
      input.style.marginBottom = '1rem';
      ['blur', 'change', 'focus', 'focusout'].forEach(evt => {
        input.addEventListener(evt, e => e.stopPropagation());
      });
      input.addEventListener('input', () => filterStadtLabels(dialog, input.value));
      dialog.insertAdjacentElement('afterbegin', input);
    }

    // B3: Event-Delegation statt Listener pro Checkbox — überlebt AJAX-Updates,
    // bei denen Shopify die Checkbox-Knoten austauscht.
    dialog.addEventListener('change', (e) => {
      if (e.target.matches('input[type="checkbox"]')) {
        dialog.removeAttribute('open');
      }
    });

    highlightActiveStadt(dialog);
  }

  function filterStadtLabels(dialog, value) {
    const term = value.toLowerCase().trim();
    dialog.querySelectorAll('label.facet-dialog-option').forEach(label => {
      const txt = label.textContent.toLowerCase();
      const cb  = document.getElementById(label.getAttribute('for'));
      const visible = txt.indexOf(term) === 0;
      label.style.display = visible ? '' : 'none';
      if (cb) cb.style.display = visible ? '' : 'none';
    });
  }

  function highlightActiveStadt(dialog) {
    if (!dialog) return;
    dialog.querySelectorAll('label.facet-dialog-option.active').forEach(l => l.classList.remove('active'));
    dialog.querySelectorAll('input[type="checkbox"]:checked').forEach(cb => {
      const lab = dialog.querySelector(`label.facet-dialog-option[for="${cb.id}"]`);
      if (lab) lab.classList.add('active');
    });
  }


  // ─── 7. Kalender init ────────────────────────────────────────────────────
  function initDateCalendar() {
    const dialog = document.querySelector('facet-dialog[id*="date_filter"]');
    if (!dialog) return;

    if (dialog.dataset.fdcInit) {
      if (dialog._fdcInstance) {
        dialog._fdcInstance.refresh();
      }
      return;
    }

    dialog.dataset.fdcInit = '1';
    injectCalendarStyles();
    dialog._fdcInstance = new FacetDateCalendar(dialog);
  }


  // ─── 8. Newsletter-Formular ──────────────────────────────────────────────
  // B8: Versuchszähler statt unbegrenztem Polling, falls das Formular auf
  // der aktuellen Seite gar nicht existiert.
  function addNewsletterClass(attemptsLeft) {
    if (attemptsLeft === undefined) attemptsLeft = 20;
    const form = document.querySelector('form[id*="omnisend-form"][id*="submit-form"]');
    if (form) {
      form.classList.add('newsletter-formular');
      return;
    }
    if (attemptsLeft <= 0) return;
    setTimeout(() => addNewsletterClass(attemptsLeft - 1), 1000);
  }


  // ─── 9. MutationObserver ─────────────────────────────────────────────────
  // Prio 1 / Ebene 2: Observer während eigener Schreibvorgänge pausieren
  // und Mutation-Bursts per requestAnimationFrame bündeln.
  let _observer = null;
  let _syncScheduled = false;

  function withObserverPaused(fn) {
    if (_observer) _observer.disconnect();
    try {
      fn();
    } finally {
      if (_observer) _observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  function syncAll() {
    const stadtDialog = document.getElementById('filter-dialog-filter.p.m.custom.stadt');
    initDateCalendar();
    initStadtFilter();
    patchActiveFacets();
    highlightActiveStadt(stadtDialog);
  }

  function scheduleSync() {
    if (_syncScheduled) return;
    _syncScheduled = true;
    requestAnimationFrame(() => {
      _syncScheduled = false;
      withObserverPaused(syncAll);
    });
  }

  function observeDOM() {
    _observer = new MutationObserver(scheduleSync);
    _observer.observe(document.body, { childList: true, subtree: true });
  }


  // ─── 10. Ajaxinate ───────────────────────────────────────────────────────
  function initAjaxinate(attemptsLeft) {
    if (attemptsLeft === undefined) attemptsLeft = 20;
    if (typeof Ajaxinate !== 'undefined') {
      new Ajaxinate();
      return;
    }
    if (attemptsLeft > 0) {
      setTimeout(() => initAjaxinate(attemptsLeft - 1), 250);
    } else {
      console.warn('[dp] Ajaxinate wurde nicht geladen');
    }
  }


  // ─── 11. Start ───────────────────────────────────────────────────────────
  function start() {
    initDateCalendar();
    initStadtFilter();
    patchActiveFacets();
    observeDOM();
    setTimeout(() => addNewsletterClass(), 2000);
    initAjaxinate();
  }

  // B11: sicherstellen, dass document.body existiert, bevor der Observer
  // darauf beobachtet (relevant falls die Datei ohne `defer` eingebunden wird).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Produktbeschreibung: "mehr lesen"
  // ───────────────────────────────────────────────────────────────────────────
  function initReadMore() {
    document.querySelectorAll('.product-info__description .prose').forEach(function (prose) {
      if (prose.dataset.readmore) return;
      prose.dataset.readmore = 'true';
      prose.classList.add('dp-readmore');

      // Nur kürzen, wenn der Text wirklich länger ist
      if (prose.scrollHeight <= prose.clientHeight + 2) {
        prose.classList.remove('dp-readmore');
        return;
      }

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'dp-readmore__toggle';
      button.setAttribute('aria-expanded', 'false');
      button.textContent = '… mehr lesen';

      button.addEventListener('click', function () {
        var expanded = prose.classList.toggle('is-expanded');
        button.setAttribute('aria-expanded', expanded);
        button.textContent = expanded ? 'weniger anzeigen' : '… mehr lesen';
      });

      prose.after(button);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReadMore);
  } else {
    initReadMore();
  }

  // Bindestrich in Collection-Card-Titeln nur auf Desktop entfernen
  const dpMq = window.matchMedia('(min-width: 1000px)');

  function dpUpdateCardTitles() {
    document.querySelectorAll('.collection-card-title').forEach(function (el) {
      if (!el.dataset.originalText) el.dataset.originalText = el.textContent;
      el.textContent = dpMq.matches
        ? el.dataset.originalText.replace(/-/g, '')
        : el.dataset.originalText;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dpUpdateCardTitles);
  } else {
    dpUpdateCardTitles();
  }
  dpMq.addEventListener('change', dpUpdateCardTitles);

})();